import { Store } from 'vuex';
import VueRouter from 'vue-router';

/**
 * Runtime context for managing dependencies that would otherwise create circular imports.
 * This allows services like the HTTP client to access the store and router without
 * directly importing them, breaking circular dependency chains.
 */
export interface IRuntimeContext {
  store?: Store<any>;
  router?: VueRouter;
}

let runtimeContext: IRuntimeContext = {};

/**
 * Set the runtime context with store and router instances.
 * This should be called once during application initialization.
 */
export function setRuntimeContext(context: IRuntimeContext): void {
  runtimeContext = { ...context };
  console.log('[RuntimeContext] Context initialized:', {
    hasStore: !!context.store,
    hasRouter: !!context.router
  });
}

/**
 * Get the current runtime context.
 * Returns empty object if context hasn't been initialized yet.
 */
export function getRuntimeContext(): IRuntimeContext {
  return runtimeContext;
}

/**
 * Get the store instance from runtime context.
 * Returns null if store hasn't been initialized yet.
 */
export function getStore(): Store<any> | null {
  return runtimeContext.store || null;
}

/**
 * Get the router instance from runtime context.
 * Returns null if router hasn't been initialized yet.
 */
export function getRouter(): VueRouter | null {
  return runtimeContext.router || null;
}

/**
 * Check if the runtime context has been fully initialized.
 */
export function isRuntimeContextReady(): boolean {
  return !!(runtimeContext.store && runtimeContext.router);
}